import Layout from "@/components/layout/Layout";
import React, { useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CalendarIcon, Download, X, Filter, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { Link, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { enrichProcurementWithSourceName } from "@/services/utils";
import { useAuth } from "@/contexts/AuthContext";
import * as XLSX from "xlsx";

const ProcurementFilter: React.FC = () => {
  const { userRole } = useAuth();
  const [sourceType, setSourceType] = useState<string>("");
  const [sourceId, setSourceId] = useState<string>("");
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();

  // Only allow admin users
  if (userRole !== 'admin') {
    return <Navigate to="/procurement" replace />;
  }

  // Fetch sources based on type
  const fetchSources = async () => {
    if (!sourceType) return [];
    
    if (sourceType === 'supplier') {
      const { data, error } = await supabase
        .from('suppliers')
        .select('id, name')
        .eq('status', 'active')
        .order('name');
      if (error) throw error;
      return data || [];
    } else if (sourceType === 'agent') {
      const { data, error } = await supabase
        .from('agents')
        .select('id, name')
        .eq('status', 'active')
        .order('name');
      if (error) throw error;
      return data || [];
    } else if (sourceType === 'farm') {
      const { data, error } = await supabase
        .from('farms')
        .select('id, farmer_name')
        .order('farmer_name');
      if (error) throw error;
      return data || [];
    }
    return [];
  };

  const { data: sources = [] } = useQuery({
    queryKey: ['procurement-filter-sources', sourceType],
    queryFn: fetchSources,
    enabled: !!sourceType
  });

  // Fetch filtered procurements
  const { data: procurements = [], isLoading, refetch } = useQuery({
    queryKey: ['filtered-procurements', sourceType, sourceId, startDate, endDate],
    queryFn: async () => {
      let query = supabase
        .from('procurements')
        .select(`
          *,
          requestor:user_profiles!fk_procurements_requestor(first_name, last_name),
          costs:procurement_costs(*),
          finance:procurement_finance(*),
          items:procurement_items(*)
        `)
        .order('created_at', { ascending: false });

      if (sourceType) {
        query = query.eq('source_type', sourceType);
      }
      if (sourceId) {
        query = query.eq('source_id', sourceId);
      }
      if (startDate) {
        query = query.gte('created_at', startDate.toISOString());
      }
      if (endDate) {
        const endOfDay = new Date(endDate);
        endOfDay.setHours(23, 59, 59, 999);
        query = query.lte('created_at', endOfDay.toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;

      // Enrich with source names
      const enriched = await Promise.all(
        (data || []).map(async (proc) => await enrichProcurementWithSourceName(proc))
      );

      return enriched;
    },
    enabled: !!(sourceType || startDate || endDate)
  });

  const handleClearFilters = () => {
    setSourceType("");
    setSourceId("");
    setStartDate(undefined);
    setEndDate(undefined);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, any> = {
      initiated: "default",
      approved: "default",
      rejected: "destructive",
      finance_approved: "default",
      warehouse_confirmed: "default",
      completed: "default",
      cancelled: "destructive"
    };
    return <Badge variant={variants[status] || "default"}>{status.replace(/_/g, ' ')}</Badge>;
  };

  const exportToCSV = () => {
    if (!procurements || procurements.length === 0) return;

    const csvData = procurements.map(proc => ({
      'ID': proc.id,
      'Source Type': proc.source_type,
      'Source Name': proc.source_name || 'N/A',
      'Product': proc.product_name || 'N/A',
      'Quantity': proc.total_quantity || 0,
      'Amount': proc.total_amount || 0,
      'Status': proc.status,
      'Created Date': proc.created_at ? format(new Date(proc.created_at), 'PPP') : 'N/A',
      'Expected Delivery': proc.expected_delivery_date ? format(new Date(proc.expected_delivery_date), 'PPP') : 'N/A',
      'Requestor': proc.requestor ? `${proc.requestor.first_name || ''} ${proc.requestor.last_name || ''}`.trim() || 'N/A' : 'N/A'
    }));

    const csv = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `procurements_filtered_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  const exportToExcel = () => {
    if (!procurements || procurements.length === 0) return;

    const excelData = procurements.map(proc => ({
      'ID': proc.id,
      'Source Type': proc.source_type,
      'Source Name': proc.source_name || 'N/A',
      'Product': proc.product_name || 'N/A',
      'Quantity': proc.total_quantity || 0,
      'Amount': proc.total_amount || 0,
      'Status': proc.status,
      'Created Date': proc.created_at ? format(new Date(proc.created_at), 'PPP') : 'N/A',
      'Expected Delivery': proc.expected_delivery_date ? format(new Date(proc.expected_delivery_date), 'PPP') : 'N/A',
      'Requestor': proc.requestor ? `${proc.requestor.first_name || ''} ${proc.requestor.last_name || ''}`.trim() || 'N/A' : 'N/A'
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Procurements');
    XLSX.writeFile(wb, `procurements_filtered_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  };

  const hasActiveFilters = sourceType || sourceId || startDate || endDate;

  return (
    <Layout>
      <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Filter Procurements</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filter Options
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Source Type */}
            <div className="space-y-2">
              <Label>Source Type</Label>
              <Select value={sourceType} onValueChange={(value) => {
                setSourceType(value);
                setSourceId("");
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select source type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="supplier">Supplier</SelectItem>
                  <SelectItem value="agent">Agent</SelectItem>
                  <SelectItem value="farm">Farm</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Source Name */}
            <div className="space-y-2">
              <Label>Source Name</Label>
              <Select 
                value={sourceId} 
                onValueChange={setSourceId}
                disabled={!sourceType}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  {sources.map((source: any) => (
                    <SelectItem key={source.id} value={source.id}>
                      {source.name || source.farmer_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Start Date */}
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* End Date */}
            <div className="space-y-2">
              <Label>End Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="flex gap-2">
            {hasActiveFilters && (
              <Button variant="outline" onClick={handleClearFilters}>
                <X className="mr-2 h-4 w-4" />
                Clear Filters
              </Button>
            )}
            {procurements.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">
                    <Download className="mr-2 h-4 w-4" />
                    Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={exportToCSV}>
                    Export as CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={exportToExcel}>
                    Export as Excel
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Results {procurements.length > 0 && `(${procurements.length})`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : !hasActiveFilters ? (
            <div className="text-center py-8 text-muted-foreground">
              Select filters to view procurements
            </div>
          ) : procurements.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No procurements found matching the selected filters
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Requestor</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {procurements.map((proc: any) => (
                    <TableRow key={proc.id}>
                      <TableCell className="font-mono text-xs">
                        {proc.id.slice(0, 8)}...
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{proc.source_name}</div>
                          <div className="text-xs text-muted-foreground capitalize">
                            {proc.source_type}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{proc.product_name || 'N/A'}</TableCell>
                      <TableCell>{proc.total_quantity || 0} kg</TableCell>
                      <TableCell>
                        {proc.total_amount ? `ETB ${proc.total_amount.toLocaleString()}` : 'N/A'}
                      </TableCell>
                      <TableCell>{getStatusBadge(proc.status)}</TableCell>
                      <TableCell>
                        {proc.created_at ? format(new Date(proc.created_at), 'PP') : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {proc.requestor ? `${proc.requestor.first_name || ''} ${proc.requestor.last_name || ''}`.trim() || 'N/A' : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Link to={`/procurements/${proc.id}`}>
                          <Button variant="outline" size="sm">
                            View Details
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      </div>
    </Layout>
  );
};

export default ProcurementFilter;
